import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Loader2 } from "lucide-react";
import SEO from "@/components/SEO";

export default function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const redirect = searchParams.get("redirect") || "/";

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate(redirect === "checkout" ? "/" : redirect);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="login-page" className="min-h-[80vh] flex items-center justify-center px-6 py-16">
      <SEO title="Sign In | AERWEAR" description="AERWEAR account and order tools." noindex />
      <div className="w-full max-w-sm">
        <h1 className="text-3xl font-black uppercase tracking-tighter font-['Cabinet_Grotesk'] text-center mb-2">Log In</h1>
        <p className="text-sm text-gray-500 font-['Outfit'] text-center mb-8">Welcome back.</p>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="text-xs uppercase tracking-[0.15em] font-semibold font-['Outfit'] mb-1.5 block">Email</label>
            <Input
              data-testid="login-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="h-12 rounded-none border-gray-300 focus:border-[#0A0A0A] font-['Outfit']"
              placeholder="you@email.com"
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.15em] font-semibold font-['Outfit'] mb-1.5 block">Password</label>
            <Input
              data-testid="login-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className="h-12 rounded-none border-gray-300 focus:border-[#0A0A0A] font-['Outfit']"
              placeholder="Your password"
            />
          </div>

          {error && <p data-testid="login-error" className="text-xs text-red-500 font-['Outfit']">{error}</p>}

          <button
            data-testid="login-submit"
            type="submit"
            disabled={loading}
            className="w-full bg-[#0A0A0A] text-white py-4 text-sm uppercase tracking-[0.15em] font-bold hover:bg-[#1a1a1a] transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {loading && <Loader2 size={16} className="animate-spin" />}
            Log In
          </button>
        </form>

        <p className="text-sm text-gray-500 font-['Outfit'] text-center mt-6">
          Don't have an account?{" "}
          <Link to="/register" data-testid="go-register" className="text-[#0A0A0A] font-semibold underline">Sign Up</Link>
        </p>
      </div>
    </div>
  );
}
